import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Gavel, Share2, Copy, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { StatRow, Section } from "./QuarterReview";

/** Body of an annual review — shared with the public view. */
export function YearBody({ review }) {
  return (
    <>
      {review.throughline && (
        <section className="board-book-section mt-10 border-l-2 pl-5" style={{ borderColor: "#c8a44a" }}>
          <p className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: "#8a6c1f" }}>
            The throughline
          </p>
          <p className="text-xl mt-2 leading-snug italic">{review.throughline}</p>
        </section>
      )}
      {review.executive_summary && (
        <Section eyebrow="§ 1 · The year in one read" title="Executive summary">
          <p className="text-base leading-relaxed">{review.executive_summary}</p>
        </Section>
      )}
      {Array.isArray(review.quarters) && review.quarters.length > 0 && (
        <Section eyebrow="§ 2 · Quarter by quarter" title="How the year moved">
          <div className="space-y-5">
            {review.quarters.map((q, i) => (
              <div key={i}>
                <h3 className="text-lg">
                  <span className="font-mono text-xs mr-2" style={{ color: "#8a6c1f" }}>Q{q.quarter}</span>
                  {q.title}
                </h3>
                <p className="text-base leading-relaxed mt-1">{q.detail}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
      {Array.isArray(review.wins) && review.wins.length > 0 && (
        <Section eyebrow="§ 3 · Wins" title="Banked this year">
          <ul className="space-y-2 list-disc pl-5">
            {review.wins.map((w, i) => <li key={i} className="text-base leading-relaxed">{w}</li>)}
          </ul>
        </Section>
      )}
      {Array.isArray(review.misses) && review.misses.length > 0 && (
        <Section eyebrow="§ 4 · Misses" title="Named, not buried">
          <ul className="space-y-2 list-disc pl-5">
            {review.misses.map((w, i) => <li key={i} className="text-base leading-relaxed">{w}</li>)}
          </ul>
        </Section>
      )}
      {review.pattern_analysis && (
        <Section eyebrow="§ 5 · Pattern" title="What the year kept repeating">
          <p className="text-base leading-relaxed">{review.pattern_analysis}</p>
        </Section>
      )}
      {Array.isArray(review.next_year_bets) && review.next_year_bets.length > 0 && (
        <Section eyebrow="§ 6 · Next year" title="Three bets">
          <ol className="space-y-3 list-decimal pl-5">
            {review.next_year_bets.map((b, i) => <li key={i} className="text-base leading-relaxed">{b}</li>)}
          </ol>
        </Section>
      )}
      {review.chair_note && (
        <section className="board-book-section mt-14 border-t rule border-[#ddd] pt-8">
          <p className="text-base italic leading-relaxed">{review.chair_note}</p>
        </section>
      )}
    </>
  );
}

export default function YearReview() {
  const { year } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compiling, setCompiling] = useState(false);
  const [chair, setChair] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const [r, me] = await Promise.all([
      api.get(`/year-review/${year}`),
      api.get("/me").catch(() => ({ data: null })),
    ]);
    setData(r.data);
    setChair(me.data);
    setShareToken(r.data.share?.token || null);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { if (!cancelled) await load(); }
      catch (e) { console.debug("year review load failed", e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const compile = async () => {
    setCompiling(true);
    try {
      await api.post(`/year-review/${year}/compile`);
      await load();
      toast.success("Year compiled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not compile the year.");
    } finally {
      setCompiling(false);
    }
  };

  const share = async () => {
    try {
      const r = await api.post(`/year-review/${year}/share`);
      setShareToken(r.data.token);
      await navigator.clipboard.writeText(`${window.location.origin}/year/${r.data.token}`).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Public link copied.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not create a link.");
    }
  };

  if (loading) return <div className="min-h-[40vh] flex items-center justify-center text-[#8a8a8a]">Loading year…</div>;
  if (!data) return <div className="min-h-[40vh] flex items-center justify-center text-[#8a8a8a]">Not found.</div>;

  const { stats, review, quarters } = data;
  const chairName = chair?.name || (chair?.email ? chair.email.split("@")[0] : null) || "The Chair";

  return (
    <>
      <div className="no-print flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-3">
        <Link to="/board-books" className="text-xs text-[#8a8a8a] hover:text-[#c89f45] flex items-center gap-1" data-testid="year-back">
          <ArrowLeft size={12} /> Board Books
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          {review ? (
            <>
              <button type="button" onClick={share}
                className="inline-flex items-center gap-2 border border-[#2a2a2a] hover:border-[#c89f45] text-[#eaeaea] hover:text-[#c89f45] rounded-md px-4 py-3 sm:py-2 text-sm transition-colors"
                data-testid="year-share">
                {copied ? <Check size={14} /> : shareToken ? <Copy size={14} /> : <Share2 size={14} />}
                {shareToken ? "Copy public link" : "Share"}
              </button>
              <button type="button" onClick={compile} disabled={compiling}
                className="inline-flex items-center gap-2 border border-[#2a2a2a] hover:border-[#c89f45] text-[#eaeaea] hover:text-[#c89f45] rounded-md px-4 py-3 sm:py-2 text-sm transition-colors disabled:opacity-50"
                data-testid="year-recompile">
                <Sparkles size={14} /> {compiling ? "Recompiling…" : "Regenerate"}
              </button>
              <button type="button" onClick={() => window.print()}
                className="inline-flex items-center gap-2 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md px-4 py-3 sm:py-2 text-sm transition-colors"
                data-testid="year-print">
                <Printer size={14} /> Print / Save as PDF
              </button>
            </>
          ) : (
            <button type="button" onClick={compile} disabled={compiling || stats.sessions_held === 0}
              className="inline-flex items-center gap-2 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md px-4 py-3 sm:py-2 text-sm transition-colors disabled:opacity-50"
              data-testid="year-compile">
              <Sparkles size={14} /> {compiling ? "Compiling the year…" : "Compile the year"}
            </button>
          )}
        </div>
      </div>

      <article className="board-book-page max-w-3xl mx-auto bg-white text-[#111] rounded-md shadow-2xl p-6 sm:p-10 lg:p-14 print:p-0 print:shadow-none" data-testid="year-review-page">
        <section className="board-book-cover text-center py-8">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-sm border border-[#c89f45] mb-6">
            <Gavel size={22} style={{ color: "#8a6c1f" }} />
          </div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: "#8a6c1f" }}>
            Board Book · Annual review
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">{review?.headline || `${year} in the chair`}</h1>
          <p className="text-sm text-[#555] mt-4 font-mono">The year of {year}</p>
          <p className="text-sm text-[#555] mt-1 font-mono">Chair · {chairName}</p>
        </section>

        <StatRow stats={stats} />

        {review ? <YearBody review={review} /> : (
          <div className="text-center py-10" data-testid="year-not-compiled">
            <p className="text-lg">
              {stats.sessions_held > 0
                ? `${stats.sessions_held} session${stats.sessions_held === 1 ? "" : "s"} across ${quarters.filter((q) => q.stats.sessions_held).length} quarter(s).`
                : "No sessions on the record for this year yet."}
            </p>
            {stats.sessions_held > 0 && (
              <p className="text-sm text-[#555] mt-2">
                Press “Compile the year” to roll them into one annual Board Book.
              </p>
            )}
          </div>
        )}

        <Section eyebrow="§ Appendix" title="Quarters in this year">
          <ul className="space-y-2">
            {quarters.map((q) => (
              <li key={q.quarter} className="text-base" data-testid={`year-quarter-${q.quarter}`}>
                <span className="font-mono text-xs mr-2" style={{ color: "#8a6c1f" }}>Q{q.quarter}</span>
                {q.stats.sessions_held
                  ? `${q.stats.sessions_held} session${q.stats.sessions_held === 1 ? "" : "s"}`
                  : "no sessions"}
                {q.headline ? ` · ${q.headline}` : q.stats.sessions_held ? " · not compiled" : ""}
              </li>
            ))}
          </ul>
        </Section>
      </article>
    </>
  );
}
